import { useCallback, useEffect, useRef, useState } from 'react';
import type { Entity } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { fetchMyGroups } from '../utils/myGroups';

const EMPTY_POLL_MS = 10_000;
const CATCH_UP_POLL_MS = 5_000;
const CATCH_UP_POLL_FAST_MS = 2_000;
const MAX_EMPTY_POLL_MS = 60_000;
const MAX_CATCH_UP_POLL_MS = 600_000;

export function useMyGroups(): {
  groups: Entity[];
  memberOfCount: number;
  loading: boolean;
  syncing: boolean;
  syncTimedOut: boolean;
  error: Error | undefined;
  refresh: () => void;
} {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [groups, setGroups] = useState<Entity[]>([]);
  const [memberOfCount, setMemberOfCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncTimedOut, setSyncTimedOut] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const [tick, setTick] = useState(0);
  const startedAt = useRef(Date.now());

  const refresh = useCallback(() => {
    startedAt.current = Date.now();
    setSyncTimedOut(false);
    setLoading(true);
    setTick(n => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async () => {
      try {
        if (tick === 0) {
          setLoading(true);
        }
        const profile = await identityApi.getBackstageIdentity();
        const result = await fetchMyGroups(catalogApi, profile.userEntityRef);
        if (cancelled) {
          return;
        }
        setGroups(result.groups);
        setMemberOfCount(result.memberOfCount);
        setError(undefined);

        const elapsed = Date.now() - startedAt.current;
        const waitingForCatalogGroups =
          result.groups.length < result.memberOfCount;
        const stillEmptyNoMemberOf =
          result.memberOfCount === 0 && result.groups.length === 0;
        const hasPartialList =
          result.groups.length > 0 && waitingForCatalogGroups;

        let maxWaitMs = 0;
        if (waitingForCatalogGroups) {
          maxWaitMs = MAX_CATCH_UP_POLL_MS;
        } else if (stillEmptyNoMemberOf) {
          maxWaitMs = MAX_EMPTY_POLL_MS;
        }
        let pollMs = EMPTY_POLL_MS;
        if (waitingForCatalogGroups) {
          pollMs = hasPartialList ? CATCH_UP_POLL_FAST_MS : CATCH_UP_POLL_MS;
        }

        const withinWindow = elapsed < maxWaitMs;
        const showSyncing =
          (stillEmptyNoMemberOf && withinWindow) ||
          (waitingForCatalogGroups && withinWindow && result.groups.length === 0);

        setSyncing(showSyncing);
        setSyncTimedOut(
          (stillEmptyNoMemberOf || waitingForCatalogGroups) &&
            !withinWindow &&
            result.groups.length === 0,
        );

        if (waitingForCatalogGroups && withinWindow) {
          timer = setTimeout(() => setTick(n => n + 1), pollMs);
        } else if (stillEmptyNoMemberOf && withinWindow) {
          timer = setTimeout(() => setTick(n => n + 1), pollMs);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setSyncing(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [identityApi, catalogApi, tick]);

  return {
    groups,
    memberOfCount,
    loading,
    syncing,
    syncTimedOut,
    error,
    refresh,
  };
}
